/**
 * JWT utility functions for decoding tokens
 */

export interface SubscriptionData {
  has_active_plan: boolean;
  plan_name: string | null;
  plan_description: string | null;
  duration: string | null;
  start_date: string | null;
  end_date: string | null;
  available_features: string[];
  consumable_limits: Record<string, number>;
  user_id: string;
  app_id: string;
}

export interface JWTPayload {
  exp: number;
  sub: string;
  subscription?: SubscriptionData;
}

/**
 * Decode JWT token without verification
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decode base64
    const decodedPayload = atob(paddedPayload);
    
    // Parse JSON
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Get subscription data from JWT token
 * @param token JWT token string
 * @returns Subscription data or null if not found
 */
export function getSubscriptionFromToken(token: string): SubscriptionData | null {
  const payload = decodeJWT(token);
  return payload?.subscription || null;
}

export function getUserIdFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.sub || null;
}

/**
 * Check if user has an active subscription
 * @param token JWT token string
 * @returns True if user has active subscription
 */
export function hasActiveSubscription(token: string): boolean {
  const subscription = getSubscriptionFromToken(token);
  return subscription?.has_active_plan || false;
}

/**
 * Get current plan name from token
 * @param token JWT token string
 * @returns Plan name or null
 */
export function getCurrentPlanName(token: string): string | null {
  const subscription = getSubscriptionFromToken(token);
  return subscription?.plan_name || null;
}
